import lodash from 'lodash'
import store from '@/store'
import * as ecircularAPI from '@/api/ecircular/request'
import * as departmentAPI from '@/api/departments/request'
import { utils as formUtils } from '@/plugin/InputChange'
import router from '@/router'
import types from '@/store/mutation-types'
import { forceUpdateVueObject } from '@/store/util'
import navigation from '@/utils/navigation'

import { E_CIRCULAR_STATUS, E_CIRCULAR_TYPES, E_CIRCULAR_USER_TYPE_CODES, normalizeMarketingTypeParamsToCodes } from '@/modules/eCircular/consts'
import { getCategoryOptions } from '@/modules/eCircular/utils'

function getDefaultState () {
  return {
    currentECircular: {
      id: '',
      statue: '',
      zone: '',
      category: '',
      buildings: [],
      signatureCode: '',
      description: '',
      scheduleDate: '',
      subject: '',
    },
    searchRecipientsResult: [],
    /** Search UI conditions keyed by eCircular id (prevents cross-ECircular pollution). */
    searchRecipientsConditionsById: {},
    searchVendorRecipientsConditionsById: {},
    addedRecipients: [],
    lastECircularSearchAttrs: {},
    lastSearchedECirculars: [],
    searchCount: 0,
    navigatingToRecipientOwner: false,
    eCircularFilterConditions: {},
    eCircularTableOrder: {},
    categories: [],
  }
}

function getConditionsById (state, bucketKey, id) {
  if (!id) return {}
  return (state[bucketKey] && state[bucketKey][id]) || {}
}

function getCurrentConditions (state, bucketKey) {
  const id = lodash.get(state, 'currentECircular.id')
  return getConditionsById(state, bucketKey, id)
}

function saveConditionsById (state, bucketKey, data) {
  const id = lodash.get(data, 'ecircularId') || lodash.get(state, 'currentECircular.id')
  if (id) {
    forceUpdateVueObject(state, [bucketKey, id], data)
  }
}

const getters = {
  currentECircular: state => state.currentECircular,
  navigatingToRecipientOwner: state => state.navigatingToRecipientOwner,
  searchRecipientsConditions: state => getCurrentConditions(state, 'searchRecipientsConditionsById'),
  searchRecipientsConditionsById: state => (id) => getConditionsById(state, 'searchRecipientsConditionsById', id),
  searchVendorRecipientsConditions: state => getCurrentConditions(state, 'searchVendorRecipientsConditionsById'),
  searchVendorRecipientsConditionsById: state => (id) => getConditionsById(state, 'searchVendorRecipientsConditionsById', id),
  searchRecipientsResult: state => state.searchRecipientsResult,
  addedRecipients: state => state.addedRecipients,
  lastECircularSearchAttrs: state => state.lastECircularSearchAttrs,
  lastSearchedECirculars: state => state.lastSearchedECirculars,
  lastSearchedCategories: state => state.lastSearchedCategories,
  searchCount: state => state.searchCount,
  eCircularFilterConditions: state => state.eCircularFilterConditions,
  eCircularTableOrder: state => state.eCircularTableOrder,
  categories: state => state.categories,
}

const actions = {
  async loadECircular ({commit}, id) {
    // do not sent two api get information of ecircular, just need one api
    const ecircular = await ecircularAPI.getECircularById(id)
    commit(types.ECIRCULAR_CURRENT, ecircular)
    // ecircular.histories[0] is latest history
    if (ecircular.histories) {
      commit(types.ECIRULAR_ADDED_RECIPIENTS, ecircular.histories[0].recipientTrees)
    }
    return ecircular
  },
  async cancelECircular ({dispatch, state}) {
    await ecircularAPI.updateECircularStatus(state.currentECircular.id, E_CIRCULAR_STATUS.CANCELLED)
    formUtils.resetDirty()
    await dispatch('loadECircular', state.currentECircular.id)
    navigation.replacePage('eCircularDetail', state.currentECircular.id)
  },
  async createECircular ({commit}, params) {
    const newEcircular = await ecircularAPI.createECircular(params)
    commit(types.ECIRCULAR_CURRENT, newEcircular)
    router.push({
      name: 'createECircular',
      params: {
        id: newEcircular.id,
      },
    })
  },
  async updateECircular ({commit}, {id, name, value}) {
    const ecircular = await ecircularAPI.updateECircularById(id, { [name]: value })
    commit(types.ECIRCULAR_CURRENT, ecircular)
    return ecircular
  },
  async updateECircularAllInfo ({commit, state}) {
    const ecircular = await ecircularAPI.updateECircularById(state.currentECircular.id, state.currentECircular)
    commit(types.ECIRCULAR_CURRENT, ecircular)
    return ecircular
  },
  async searchRecipients ({commit, state}, {eCircularType = state.currentECircular.type, ...request}) {
    const searchResult = await ecircularAPI.getRecipientTrees({eCircularType, ...request})
    commit(types.ECIRCULAR_SAVE_SEARCH_RECIPIENTS, searchResult)
    return searchResult
  },
  async setECircularSearchResults ({commit}, searchItems) {
    commit(types.ECIRCULAR_SEARCH_RESULTS, searchItems)
  },
  async updateRecipients ({commit, state}, showNotification) {
    const requestBody = lodash.cloneDeep(state.currentECircular)
    requestBody.histories[0].recipientTrees = lodash.cloneDeep(state.addedRecipients)
    if (requestBody.type === E_CIRCULAR_TYPES.TENANT) {
      const conditions = getConditionsById(state, 'searchRecipientsConditionsById', requestBody.id)
      const uc = lodash.cloneDeep(
        lodash.get(conditions, 'pmCircularParams.userContactTypes') || {},
      )
      const marketingTypes = normalizeMarketingTypeParamsToCodes(
        lodash.get(conditions, 'marketingParams.marketingTypes') || [],
      )
      const pmContactTypeKeys = ['isCircular', 'isFireMarshal']
      const pmCodes = pmContactTypeKeys
        .filter((key) => uc[key])
        .map((key) => E_CIRCULAR_USER_TYPE_CODES[key])
      requestBody.userType = [...pmCodes, ...marketingTypes]
    } else if (requestBody.type === E_CIRCULAR_TYPES.VENDOR) {
      const vendorConditions = getConditionsById(state, 'searchVendorRecipientsConditionsById', requestBody.id)
      const selectedVendorTypes = vendorConditions.vendorType || []
      requestBody.userType = Array.isArray(selectedVendorTypes) ? [...selectedVendorTypes] : []
    }
    await ecircularAPI.persistRecipientsSelection(requestBody, showNotification)
    commit(types.ECIRCULAR_CURRENT, requestBody)
  },
  async getCategories ({ commit }, type) {
    const currentUser = store.getters['user/currentUser']
    const departmentIds = currentUser.departments.map(dept => dept.id)
    const results = await departmentAPI.getCategoriesByDepartment({ ids: departmentIds, isAll: true, type })
    const categoryOptions = getCategoryOptions(results)
    commit(types.ECIRCULAR_GET_CATEGORIES, categoryOptions)
  },
}

const mutations = {
  [types.ECIRCULAR_CURRENT] (state, ecircular) {
    state.currentECircular = ecircular
  },
  [types.RESET_STATE] (state) {
    Object.assign(state, { ...getDefaultState() })
  },
  [types.ECIRCULAR_UPDATE_ONE_ATTRIBUTE] (state, {name, value}) {
    const newECircular = lodash.cloneDeep(state.currentECircular)
    newECircular[name] = value
    state.currentECircular = newECircular
  },
  [types.ECIRCULAR_UPDATE_HISTORY_FIELD_PATH] (state, { index, path, newValue }) {
    if (deep(state, 'currentECircular.histories') && state.currentECircular.histories[index]) {
      const fullPath = ['histories', index, ...path]
      forceUpdateVueObject(state.currentECircular, fullPath, newValue)
    }
  },
  [types.ECIRCULAR_NAV_TO_RECIPIENT_OWNER] (state, data) {
    state.navigatingToRecipientOwner = data
  },
  [types.ECIRCULAR_SAVE_SEARCH_RECIPIENT_CONDITIONS] (state, data) {
    saveConditionsById(state, 'searchRecipientsConditionsById', data)
  },
  [types.ECIRCULAR_SAVE_SEARCH_VENDOR_RECIPIENT_CONDITIONS] (state, data) {
    saveConditionsById(state, 'searchVendorRecipientsConditionsById', data)
  },
  [types.ECIRCULAR_SAVE_SEARCH_RECIPIENTS] (state, data) {
    state.searchRecipientsResult = data
  },
  [types.ECIRULAR_ADDED_RECIPIENTS] (state, data) {
    state.addedRecipients = data
  },
  [types.ECIRCULAR_SEARCH_RESULTS] (state, data) {
    state.lastECircularSearchAttrs = data.lastECircularSearchAttrs
    state.lastSearchedECirculars = data.lastSearchedECirculars
  },
  [types.ECIRCULAR_SAVE_SEARCH_COUNT] (state, number) {
    state.searchCount = number
  },
  [types.ECIRCULAR_SAVE_ECIRCULAR_FILTER_CONDITIONS] (state, conditions) {
    state.eCircularFilterConditions = conditions
  },
  [types.ECIRCULAR_SAVE_ECIRCULAR_TABLE_ORDER] (state, order) {
    state.eCircularTableOrder = order
  },
  [types.ECIRCULAR_GET_CATEGORIES] (state, categories) {
    state.categories = categories
  },
}

export default {
  namespaced: true,
  state: getDefaultState(),
  getters,
  actions,
  mutations,
}
